/* ==========================================================================
   Royal Honey — features (V2)
   Discreet mode · quick exit · product quiz · box builder · comparator ·
   size calculator · section reveal · hero spotlight
   ========================================================================== */
(() => {
  'use strict';

  const N = (window.SR = window.SR || {});
  const U = N.utils || {};
  const root = document.documentElement;
  const define = U.define || ((name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); });
  const store = U.store || {
    get(key, fallback) { try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (err) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (err) { /* private mode */ } }
  };
  const toast = U.toast || (() => {});
  const bus = U.bus || { emit: () => {}, on: () => {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const settings = N.settings || {};
  const strings = N.strings || {};
  const money = (cents, format) => {
    const fmt = format || N.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = (Math.round(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const spaced = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      if (key === 'amount_no_decimals' || key === 'amount_no_decimals_with_comma_separator') return spaced;
      if (key === 'amount_with_comma_separator') return `${spaced},${dec}`;
      if (key === 'amount_with_apostrophe_separator') return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${dec}`;
      return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
    });
    return out;
  };
  N.money = money;

  /* ---------- Discreet mode ---------- */
  const DISCREET_KEY = 'sr-discreet';
  const realTitle = document.title;
  const setDiscreet = (on, announce) => {
    root.classList.toggle('is-discreet', on);
    document.querySelectorAll('[data-discreet-toggle]').forEach((btn) => btn.setAttribute('aria-pressed', String(on)));
    document.title = on && settings.discreetTitle ? settings.discreetTitle : realTitle;
    if (!on) document.querySelectorAll('.is-unblurred, .has-unblurred').forEach((el) => el.classList.remove('is-unblurred', 'has-unblurred'));
    try { localStorage.setItem(DISCREET_KEY, on ? '1' : '0'); } catch (err) { /* private mode */ }
    if (announce) toast(on ? strings.discreetOn : strings.discreetOff);
  };
  if (settings.discreet) {
    setDiscreet(root.classList.contains('is-discreet'), false);
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-discreet-toggle]');
      if (toggle) { e.preventDefault(); setDiscreet(!root.classList.contains('is-discreet'), true); return; }
      if (!root.classList.contains('is-discreet')) return;
      // First tap on a blurred image shows it; the next one follows the link as usual.
      // Product cards are covered by their stretched link, so the tap is located by position.
      const card = e.target.closest('.card');
      if (card && !e.target.closest('button, input, select, [data-quick-view]')) {
        const box = card.querySelector('.card__media');
        const r = box ? box.getBoundingClientRect() : null;
        if (r && !box.classList.contains('has-unblurred') && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          e.preventDefault();
          e.stopPropagation();
          box.classList.add('has-unblurred');
          box.querySelectorAll('img, svg.art').forEach((el) => el.classList.add('is-unblurred'));
          return;
        }
      }
      const media = e.target.closest('main img, main svg.art, main video, .drawer img, .qv img, .qv svg.art');
      if (media && !media.classList.contains('is-unblurred')) {
        e.preventDefault();
        e.stopPropagation();
        media.classList.add('is-unblurred');
        media.closest('.card__media, .gallery__item, [data-discreet-scope]')?.classList.add('has-unblurred');
      }
    }, true);
  }

  /* ---------- Quick exit ---------- */
  const quickExit = () => {
    root.style.visibility = 'hidden';
    document.title = settings.discreetTitle || '';
    window.location.replace(settings.quickExitUrl || 'https://www.google.fr');
  };
  if (settings.quickExit) {
    let presses = [];
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const now = Date.now();
      presses = presses.filter((t) => now - t < 900);
      presses.push(now);
      if (presses.length >= 3) quickExit();
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-quick-exit]')) return;
      e.preventDefault();
      quickExit();
    });
  }

  /* ---------- Product quiz ---------- */
  const slug = (value) => String(value || '').toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').trim().replace(/[^a-z0-9:.\-]+/g, '-').replace(/^-+|-+$/g, '');
  define('product-quiz', class extends HTMLElement {
    connectedCallback() {
      this.intro = this.querySelector('[data-quiz-intro]');
      this.form = this.querySelector('[data-quiz-form]');
      this.steps = [...this.querySelectorAll('[data-quiz-q]')];
      this.results = this.querySelector('[data-quiz-results]');
      this.grid = this.querySelector('[data-quiz-grid]');
      this.pool = this.querySelector('[data-quiz-pool]');
      this.progress = this.querySelector('[data-quiz-progress]');
      this.bar = this.querySelector('[data-quiz-bar]');
      this.back = this.querySelector('[data-quiz-back]');
      this.next = this.querySelector('[data-quiz-next]');
      this.key = this.dataset.key;
      this.index = 0;
      if (!this.steps.length || !this.form) return;
      this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.start(false));
      this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.start(true));
      const last = this.querySelector('[data-quiz-last]');
      const saved = store.get(this.key, null);
      const hasSaved = saved && Array.isArray(saved.answers) && saved.answers.length === this.steps.length;
      if (last && hasSaved) {
        last.hidden = false;
        last.addEventListener('click', () => this.showResults(saved.answers));
      }
      this.querySelector('[data-quiz-share]')?.addEventListener('change', () => this.fillTags());
      // Back from the newsletter form: show the results again with its message.
      const posted = this.querySelector('[data-quiz-posted]');
      if (posted && hasSaved) {
        requestAnimationFrame(() => {
          this.showResults(saved.answers);
          posted.closest('.quiz__signup')?.scrollIntoView({ block: 'center', behavior: 'auto' });
          if (posted.hasAttribute('tabindex')) posted.focus({ preventScroll: true });
        });
      }
      this.back.addEventListener('click', () => this.go(this.index - 1));
      this.next.addEventListener('click', () => this.advance());
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.advance(); });
      this.form.addEventListener('change', (e) => {
        const step = e.target.closest('[data-quiz-q]');
        if (!step) return;
        this.sync();
        if (step.dataset.multiple !== 'true' && e.target.checked) {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.advance(), reduceMotion.matches ? 0 : 320);
        }
      });
    }
    start(reset) {
      if (reset) this.form.reset();
      this.intro.hidden = true;
      this.results.hidden = true;
      this.form.hidden = false;
      this.go(0);
      this.reveal();
    }
    go(i) {
      clearTimeout(this.timer);
      if (i < 0) {
        this.form.hidden = true;
        this.intro.hidden = false;
        this.querySelector('[data-quiz-start]')?.focus();
        return;
      }
      this.index = Math.min(i, this.steps.length - 1);
      this.steps.forEach((step, k) => {
        step.hidden = k !== this.index;
        step.classList.toggle('is-current', k === this.index);
      });
      if (this.progress) this.progress.textContent = (this.progress.dataset.label || '').replace('%c', String(this.index + 1));
      if (this.bar) this.bar.style.width = `${((this.index + 1) / this.steps.length) * 100}%`;
      this.sync();
      this.steps[this.index].querySelector('legend')?.focus({ preventScroll: true });
      this.reveal();
    }
    sync() {
      const step = this.steps[this.index];
      const multiple = step.dataset.multiple === 'true';
      this.next.disabled = !multiple && !step.querySelector('input:checked');
      this.next.textContent = this.index === this.steps.length - 1 ? this.next.dataset.finish : this.next.dataset.next;
    }
    advance() {
      const step = this.steps[this.index];
      if (step.dataset.multiple !== 'true' && !step.querySelector('input:checked')) return;
      if (this.index < this.steps.length - 1) this.go(this.index + 1);
      else this.finish();
    }
    finish() {
      const answers = this.steps.map((step) => [...step.querySelectorAll('input:checked')].map((input) => ({ tags: input.value, label: input.dataset.label })));
      store.set(this.key, { answers, at: Date.now() });
      this.showResults(answers);
    }
    match(tag, tags, price) {
      if (tag.startsWith('prix:') || tag.startsWith('price:')) {
        const [min, max] = tag.split(':')[1].split('-').map((n) => parseFloat(n));
        return price >= (min || 0) && price <= (Number.isNaN(max) || max === undefined ? Infinity : max);
      }
      return tags.includes(`,${tag},`);
    }
    score(item, answers) {
      const tags = item.dataset.tags || ',';
      const price = parseInt(item.dataset.price || '0', 10) / 100;
      let asked = 0;
      let points = 0;
      answers.forEach((selected) => {
        const groups = selected.map((a) => String(a.tags || '').split(',').map(slug).filter(Boolean)).filter((g) => g.length);
        if (!groups.length) return;
        asked += 1;
        points += groups.filter((g) => g.some((tag) => this.match(tag, tags, price))).length / groups.length;
      });
      return asked ? points / asked : 0;
    }
    fillTags() {
      const input = this.querySelector('[data-quiz-tags]');
      if (!input) return;
      const tags = ['newsletter', 'quiz'];
      if (this.querySelector('[data-quiz-share]')?.checked && this.answers) {
        this.answers.flat().forEach((a) => String((a && a.tags) || '').split(',').map(slug).filter(Boolean).forEach((tag) => {
          const t = `quiz-${tag.replace(/[:.]/g, '-')}`.slice(0, 60);
          if (!tags.includes(t)) tags.push(t);
        }));
      }
      input.value = tags.slice(0, 24).join(',');
    }
    showResults(answers) {
      this.answers = answers;
      this.fillTags();
      const items = this.pool ? [...this.pool.content.children] : [];
      const ranked = items
        .map((item, order) => ({ item, order, score: this.score(item, answers), available: item.dataset.available !== 'false' }))
        .sort((a, b) => (b.score - a.score) || (Number(b.available) - Number(a.available)) || (a.order - b.order))
        .slice(0, parseInt(this.dataset.count || '4', 10));
      const best = ranked.length ? ranked[0].score : 0;
      const fallback = this.querySelector('[data-quiz-fallback]');
      if (fallback) fallback.hidden = best > 0;
      this.grid.replaceChildren(...ranked.map(({ item, score }) => {
        const li = item.cloneNode(true);
        li.removeAttribute('data-tags');
        if (this.dataset.showMatch === 'true' && score > 0) {
          const badge = document.createElement('span');
          badge.className = 'badge badge--match';
          badge.textContent = (this.dataset.match || '%s %').replace('%s', String(Math.round(score * 100)));
          li.querySelector('.card__badges')?.prepend(badge);
        }
        return li;
      }));
      const chosen = this.querySelector('[data-quiz-chosen]');
      if (chosen) chosen.replaceChildren(...answers.flat().filter((a) => a && a.label && String(a.tags || '').trim()).map((a) => {
        const li = document.createElement('li');
        li.className = 'chip';
        li.textContent = a.label;
        return li;
      }));
      this.intro.hidden = true;
      this.form.hidden = true;
      this.results.hidden = false;
      bus.emit('cards:loaded', this.grid);
      this.querySelector('[data-quiz-results-title]')?.focus({ preventScroll: true });
      this.reveal();
    }
    reveal() {
      const top = this.getBoundingClientRect().top;
      const offset = parseInt(getComputedStyle(root).getPropertyValue('--header-h'), 10) || 64;
      if (top < offset || top > window.innerHeight * 0.5) window.scrollTo({ top: window.scrollY + top - offset - 16, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  });

  /* ---------- Box builder ---------- */
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  define('box-builder', class extends HTMLElement {
    connectedCallback() {
      this.max = parseInt(this.dataset.slots || '3', 10);
      this.chosen = [];
      this.slotList = this.querySelector('[data-box-slots]');
      this.totalEl = this.querySelector('[data-box-total]');
      this.countEl = this.querySelector('[data-box-count]');
      this.hintEl = this.querySelector('[data-box-hint]');
      this.nextEl = this.querySelector('[data-box-next]');
      this.submit = this.querySelector('[data-box-submit]');
      this.tiers = [...this.querySelectorAll('[data-tier-count]')].map((li) => ({ li, count: parseInt(li.dataset.tierCount, 10), label: li.dataset.tierLabel }))
        .sort((a, b) => a.count - b.count);
      this.closeIcon = this.querySelector('template[data-box-close]')?.innerHTML || '×';
      this.addEventListener('click', (e) => {
        const add = e.target.closest('[data-box-add]');
        if (add) { this.add(add.closest('[data-box-item]'), add); return; }
        const remove = e.target.closest('[data-box-remove]');
        if (remove) this.remove(parseInt(remove.dataset.boxRemove, 10));
      });
      this.submit?.addEventListener('click', () => this.addToCart());
      this.querySelector('[data-box-jump]')?.addEventListener('click', () => {
        const panel = this.querySelector('.boxb__panel');
        const offset = parseInt(getComputedStyle(root).getPropertyValue('--header-h'), 10) || 64;
        if (panel) window.scrollTo({ top: window.scrollY + panel.getBoundingClientRect().top - offset - 40, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      this.render();
    }
    add(item, button) {
      if (!item) return;
      if (this.chosen.length >= this.max) { toast(this.dataset.full); return; }
      const field = item.querySelector('[data-box-variant]');
      const option = field && field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      const id = parseInt(option ? option.value : '', 10);
      if (!id) { toast(this.dataset.demo); }
      if (option && option.disabled) return;
      this.chosen.push({
        id: id || 0,
        price: parseInt((option && option.dataset.price) || '0', 10),
        title: item.dataset.title || '',
        variant: field && field.tagName === 'SELECT' ? option.textContent.trim() : '',
        media: item.querySelector('.boxb__media')?.innerHTML || ''
      });
      this.render(this.chosen.length - 1);
      button?.classList.remove('is-pop');
      void button?.offsetWidth;
      button?.classList.add('is-pop');
    }
    remove(index) {
      this.chosen.splice(index, 1);
      this.render();
    }
    render(fresh) {
      const n = this.chosen.length;
      [...(this.slotList?.children || [])].forEach((li, i) => {
        const c = this.chosen[i];
        li.classList.toggle('is-filled', Boolean(c));
        li.classList.toggle('is-fresh', fresh === i);
        li.innerHTML = c
          ? `<span class="boxb__slot-media">${c.media}</span><span class="visually-hidden">${esc(c.title)} ${esc(c.variant)}</span><button type="button" class="boxb__remove" data-box-remove="${i}" aria-label="${esc((this.dataset.removeLabel || '%s').replace('%s', c.title))}">${this.closeIcon}</button>`
          : `<span class="boxb__slot-num">${i + 1}</span>`;
      });
      if (this.countEl) this.countEl.textContent = `${n} / ${this.max}`;
      const miniCount = this.querySelector('[data-box-mini-count]');
      if (miniCount) miniCount.textContent = `${n} / ${this.max}`;
      this.classList.toggle('has-items', n > 0);
      const left = this.max - n;
      if (this.hintEl) this.hintEl.textContent = left <= 0 ? this.dataset.ready : left === 1 ? this.dataset.leftOne : (this.dataset.leftMany || '').replace('%n', left);
      this.classList.toggle('is-full', left <= 0);
      const total = this.chosen.reduce((sum, c) => sum + c.price, 0);
      if (this.totalEl) this.totalEl.textContent = money(total);
      const miniTotal = this.querySelector('[data-box-mini-total]');
      if (miniTotal) miniTotal.textContent = money(total);
      this.tiers.forEach((t) => t.li.classList.toggle('is-reached', n >= t.count));
      if (this.nextEl) {
        const next = this.tiers.find((t) => n < t.count && t.count <= this.max);
        const gap = next ? next.count - n : 0;
        this.nextEl.textContent = next ? (gap === 1 ? this.dataset.nextOne : this.dataset.nextMany || '').replace('%n', gap).replace('%s', next.label) : '';
        this.nextEl.hidden = !next;
      }
      const real = this.chosen.every((c) => c.id > 0);
      if (this.submit) this.submit.disabled = left > 0 || !real;
    }
    async addToCart() {
      if (this.chosen.length < this.max || !this.submit || this.submit.disabled) return;
      const ref = Date.now().toString(36).slice(-5).toUpperCase();
      const name = `${this.dataset.boxName || ''} ${ref}`.trim();
      const message = (this.querySelector('[data-box-message]')?.value || '').trim();
      const prop = this.dataset.propLabel || 'Coffret';
      const items = this.chosen.map((c, i) => {
        const properties = { [prop]: name, _box: ref };
        if (i === 0 && message) properties[this.dataset.messageProp || 'Message'] = message;
        return { id: c.id, quantity: 1, properties };
      });
      const gift = this.querySelector('[data-box-gift]');
      if (gift && gift.checked) items.push({ id: parseInt(gift.value, 10), quantity: 1, properties: { [prop]: name, _box: ref } });
      this.submit.classList.add('is-loading');
      this.submit.setAttribute('aria-busy', 'true');
      try {
        await N.cart.cartPost(`${N.routes.cartAdd}.js`, { items });
        this.chosen = [];
        const field = this.querySelector('[data-box-message]');
        if (field) field.value = '';
        this.render();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && settings.cartType === 'drawer') drawer.open(this.submit);
        else window.location.href = N.routes.cart;
      } catch (err) {
        toast(err.message || strings.error);
      } finally {
        this.submit.classList.remove('is-loading');
        this.submit.removeAttribute('aria-busy');
      }
    }
  });

  /* ---------- Comparator ---------- */
  const CMP_KEY = 'sr-compare';
  const CMP_MAX = 3;
  const compare = {
    list() { const v = store.get(CMP_KEY, []); return Array.isArray(v) ? v.filter((x) => x && x.handle) : []; },
    save(list) { store.set(CMP_KEY, list.slice(0, CMP_MAX)); paintCompare(); bus.emit('compare:change', list); },
    toggle(item) {
      const list = this.list();
      const i = list.findIndex((x) => x.handle === item.handle);
      if (i > -1) { list.splice(i, 1); this.save(list); return false; }
      if (list.length >= CMP_MAX) { toast(strings.compareFull); return null; }
      list.push(item);
      this.save(list);
      return true;
    },
    remove(handle) { this.save(this.list().filter((x) => x.handle !== handle)); },
    clear() { this.save([]); }
  };
  N.compare = compare;
  function paintCompare(scope = document) {
    const handles = compare.list().map((x) => x.handle);
    scope.querySelectorAll('[data-compare-toggle]').forEach((btn) => {
      const on = handles.includes(btn.dataset.compareToggle);
      btn.setAttribute('aria-pressed', String(on));
      const label = btn.querySelector('[data-compare-label]');
      if (label) label.textContent = on ? label.dataset.labelRemove : label.dataset.labelAdd;
    });
  }
  if (settings.compare) {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-compare-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const added = compare.toggle({ handle: btn.dataset.compareToggle, title: btn.dataset.compareTitle || '', img: btn.dataset.compareImg || '' });
      if (added === true) toast(strings.compareAdded);
      else if (added === false) toast(strings.compareRemoved);
    });
    bus.on('cards:loaded', (scope) => paintCompare(scope && scope.querySelectorAll ? scope : document));
    paintCompare();
  }

  define('compare-tray', class extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelector('[data-ctray-items]');
      this.count = this.querySelector('[data-ctray-count]');
      this.openBtn = this.querySelector('[data-ctray-open]');
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-cmp-body]');
      this.cache = {};
      try { this.labels = JSON.parse(this.dataset.labels || '{}'); } catch (err) { this.labels = {}; }
      this.querySelector('[data-ctray-clear]')?.addEventListener('click', () => compare.clear());
      this.openBtn?.addEventListener('click', () => this.open(this.openBtn));
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-ctray-remove]');
        if (remove) { compare.remove(remove.dataset.ctrayRemove); return; }
        const add = e.target.closest('[data-cmp-add]');
        if (add) this.addToCart(add);
      });
      this.querySelector('[data-cmp-diff]')?.addEventListener('change', (e) => this.body.classList.toggle('is-diff', e.target.checked));
      document.addEventListener('sr:compare:change', () => this.render());
      this.render();
    }
    render() {
      const list = compare.list();
      this.hidden = list.length === 0;
      document.body.classList.toggle('has-ctray', list.length > 0);
      const L = this.labels;
      this.items.innerHTML = list.map((x) => `<li class="ctray__item">${x.img ? `<img src="${esc(x.img)}" alt="" width="40" height="52" loading="lazy">` : `<span class="ctray__initial" aria-hidden="true">${esc(x.title.slice(0, 1))}</span>`}<span class="ctray__name">${esc(x.title)}</span><button type="button" class="ctray__remove" data-ctray-remove="${esc(x.handle)}" aria-label="${esc(L.remove)} : ${esc(x.title)}">&times;</button></li>`).join('')
        + '<li class="ctray__slot" aria-hidden="true"></li>'.repeat(Math.max(0, CMP_MAX - list.length));
      if (this.count) this.count.textContent = `(${list.length})`;
      if (this.openBtn) this.openBtn.disabled = list.length < 2;
      paintCompare();
      if (this.dialog && this.dialog.open) {
        if (list.length < 2) this.dialog.close(); else this.fill();
      }
    }
    open(opener) {
      if (!this.dialog) return;
      N.openDialog ? N.openDialog(this.dialog, opener) : this.dialog.showModal();
      this.fill();
    }
    async item(handle) {
      if (this.cache[handle] !== undefined) return this.cache[handle];
      const res = await fetch(`${N.routes.root || '/'}products/${encodeURIComponent(handle)}?section_id=compare-item`);
      if (res.status === 404) { this.cache[handle] = null; return null; }
      if (!res.ok) throw new Error(String(res.status));
      const node = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-compare-item]');
      const data = node ? JSON.parse(node.textContent) : null;
      this.cache[handle] = data;
      return data;
    }
    async fill() {
      const L = this.labels;
      const list = compare.list();
      if (list.length < 2) { this.body.innerHTML = `<p class="cmp__empty">${esc(L.empty)}</p>`; return; }
      const ticket = (this.ticket = (this.ticket || 0) + 1);
      this.body.innerHTML = `<p class="cmp__loading">${esc(L.loading)}</p>`;
      const data = await Promise.all(list.map((x) => this.item(x.handle).catch(() => undefined)));
      if (ticket !== this.ticket) return;
      const gone = list.filter((x, i) => data[i] === null).map((x) => x.handle);
      if (gone.length) { compare.save(list.filter((x) => !gone.includes(x.handle))); return; }
      this.body.innerHTML = this.table(data.filter(Boolean));
    }
    table(items) {
      const L = this.labels;
      const none = esc(L.none || '—');
      const rows = [];
      const row = (label, cells) => {
        if (cells.every((c) => c === null || c === undefined || c === '')) return;
        const values = cells.map((c) => (c === null || c === undefined || c === '' ? none : c));
        const same = values.every((v) => v === values[0]);
        rows.push(`<tr${same ? ' class="is-same"' : ''}><th scope="row">${esc(label)}</th>${values.map((v) => `<td>${v}</td>`).join('')}</tr>`);
      };
      const specNames = [];
      items.forEach((it) => (it.specs || []).forEach(([k]) => { if (k && !specNames.includes(k)) specNames.push(k); }));
      const head = `<tr><td class="cmp__corner"></td>${items.map((it) => `<th scope="col" class="cmp__product"><a class="cmp__media" href="${esc(it.url)}" tabindex="-1" aria-hidden="true">${it.image ? `<img src="${esc(it.image)}" alt="" loading="lazy">` : (it.art || '')}</a><a class="cmp__name" href="${esc(it.url)}">${esc(it.title)}</a></th>`).join('')}</tr>`;
      row(L.price, items.map((it) => `<span class="cmp__price">${esc(it.price)}</span>${it.compareAt ? ` <s class="cmp__was">${esc(it.compareAt)}</s>` : ''}`));
      row(L.rating, items.map((it) => (it.rating ? `<span class="cmp__stars" style="--r:${Number(it.rating) / 5}" aria-hidden="true"></span> ${String(Number(it.rating).toFixed(1)).replace('.', ',')}${it.ratingCount ? ` <span class="cmp__muted">(${it.ratingCount})</span>` : ''}` : null)));
      row(L.type, items.map((it) => esc(it.type)));
      specNames.forEach((name) => row(name, items.map((it) => { const hit = (it.specs || []).find(([k]) => k === name); return hit ? esc(hit[1]) : null; })));
      row(L.colors, items.map((it) => esc(it.colors)));
      row(L.sizes, items.map((it) => esc(it.sizes)));
      row(L.stock, items.map((it) => (it.available ? `<span class="cmp__ok">${esc(L.inStock)}</span>` : esc(L.outStock))));
      const actions = `<tr class="cmp__actions"><td></td>${items.map((it) => `<td>${it.available && it.single ? `<button type="button" class="btn btn--primary btn--sm" data-cmp-add="${esc(it.variantId)}">${esc(L.add)}</button>` : `<a class="btn btn--ghost btn--sm" href="${esc(it.url)}">${esc(L.view)}</a>`}<button type="button" class="link cmp__remove" data-ctray-remove="${esc(it.handle)}">${esc(L.remove)}</button></td>`).join('')}</tr>`;
      return `<div class="cmp__scroll"><table class="cmp__table" style="--cols:${items.length}"><thead>${head}</thead><tbody>${rows.join('')}${actions}</tbody></table></div>`;
    }
    async addToCart(button) {
      button.classList.add('is-loading');
      try {
        await N.cart.cartPost(`${N.routes.cartAdd}.js`, { items: [{ id: parseInt(button.dataset.cmpAdd, 10), quantity: 1 }] });
        this.dialog?.close();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && settings.cartType === 'drawer') drawer.open(); else window.location.href = N.routes.cart;
      } catch (err) {
        toast(err.message || strings.error);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  });

  /* ---------- Copy a code ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    try { await navigator.clipboard.writeText(btn.dataset.copy); } catch (err) { /* manual copy */ }
    btn.classList.add('is-copied');
    toast(btn.dataset.copied || btn.dataset.copy);
    setTimeout(() => btn.classList.remove('is-copied'), 1800);
  });

  /* ---------- Section reveal ---------- */
  if (settings.reveal === 'soft' && !reduceMotion.matches && 'IntersectionObserver' in window && !(window.Shopify && window.Shopify.designMode)) {
    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')].slice(1)
      .concat([...document.querySelectorAll('.shopify-section-group-footer-group')]);
    const viewport = window.innerHeight;
    const done = (el) => {
      el.classList.remove('reveal-wait', 'is-in');
      el.querySelectorAll('[style*="--ri"]').forEach((n) => n.style.removeProperty('--ri'));
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        io.unobserve(el);
        el.classList.add('is-in');
        setTimeout(() => done(el), 1400);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    sections.forEach((el) => {
      if (el.getBoundingClientRect().top < viewport * 0.92) return;
      el.querySelectorAll('.grid > li, .rail__track > *').forEach((item, i) => { if (i < 8) item.style.setProperty('--ri', i); });
      el.classList.add('reveal-wait');
      io.observe(el);
    });
    root.classList.add('has-reveal');
  }

  /* ---------- Theme editor: show the question being edited ---------- */
  document.addEventListener('shopify:block:select', (e) => {
    const step = e.target.closest && e.target.closest('[data-quiz-q]');
    const quiz = step && step.closest('product-quiz');
    if (!quiz || !quiz.steps || !quiz.form) return;
    quiz.intro.hidden = true;
    quiz.results.hidden = true;
    quiz.form.hidden = false;
    quiz.go(quiz.steps.indexOf(step));
  });

})();
